// Per-field Trade-row cells. The MVP TradeBand renders the Trade section
// as 8 sub-rows (Instrument / Contract / Expiry / Notional / Strike /
// Upper Barrier / Lower Barrier / Pay Type), each with the field LABEL in
// the page-level label column and the field control as the cell content
// per instrument. This file exports those per-field cells so the page
// composition can place each one in its own BandRow.
//
// Disabled cells (e.g. Lower Barrier for OneTouch (Upper)) render an em-dash
// placeholder so the column stays aligned — matches MVP TradeBand NumInput.
import { FC, useContext } from 'react';
import { Box, IconButton, MenuItem, Select, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faTrash } from '@fortawesome/free-solid-svg-icons';

import DatePicker from '../../date-picker';
import EditableNumber from '../../editable-number';
import {
  CALL_PUTS,
  CONTRACT_LABELS,
  CONTRACT_SCHEMA,
  CallPut,
  ContractKind,
  Instrument,
  PAY_TYPES,
  PayType,
  Token,
} from '../../../../pages/exotics-pricer/contracts';
import { PricingContext } from '../../../../providers/exotics-pricing-provider';
import { useAvailableTokens } from '../../../../providers/exotics-tokens/use-available-tokens';
import { useListedExpiries } from '../../../../providers/exotics-tokens/use-listed-expiries';

import { tightSelectSx } from './index';

// Fallback while the registry call is in flight on first load.
const TOKEN_OPTIONS_FALLBACK: Token[] = ['BTC', 'ETH'];
const CONTRACT_OPTIONS = Object.entries(CONTRACT_LABELS) as [ContractKind, string][];

const useInstrument = (instrumentId: string) => {
  const { instruments, patchInstrument } = useContext(PricingContext);
  const inst = instruments.find((i) => i.id === instrumentId);
  const patch = (p: Partial<Instrument>) => patchInstrument(instrumentId, p);
  return { inst, patch };
};

// Tab-DOWN-then-across ordering: a trader entering a trade fills one
// instrument's column top-to-bottom (Instrument → Contract → Expiry →
// Notional → ... → Pay Type) before moving to the next column, rather than
// the DOM's natural row-major order (which tabs across every instrument's
// Instrument cell, then every Contract cell, etc). Explicit tabIndex is the
// only way to reorder tab traversal without restructuring the grid into
// per-instrument DOM containers.
//
// COLUMN_STEP spaces each instrument's block of tab stops far enough apart
// that ROW_STEP-spaced row slots (with room for a sub-slot, e.g. Notional's
// token dropdown + amount field) never collide between columns.
const ROW = {
  instrument: 0,
  contract: 1,
  expiry: 2,
  notional: 3,
  strike: 4,
  callPut: 5,
  upper: 6,
  lower: 7,
  payType: 8,
} as const;
const ROW_STEP = 10;
const COLUMN_STEP = 100;
const tabIndexFor = (columnIndex: number, row: number, subslot = 0): number =>
  1 + columnIndex * COLUMN_STEP + row * ROW_STEP + subslot;

const Disabled: FC = () => (
  <Box
    sx={{
      px: 0.75,
      py: 0.5,
      color: 'text.disabled',
      fontFamily: 'Inter',
      fontSize: 12,
      textAlign: 'right',
    }}
  >
    —
  </Box>
);

// ────────────────────────────────────────────────────────────────────────
// Instrument: [token select] / [USDC select] [duplicate] [delete]
// ────────────────────────────────────────────────────────────────────────

export interface InstrumentCellProps {
  instrumentId: string;
  columnIndex: number;
  atCapacity: boolean;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
}

// Layout verbatim from immersive_gui/ui/src/components/TradeBand.jsx:109-126:
//   [token Select — flex:1 grows] / [USDC Select — fixed 82px] [copy][trash]
// Token grows so the cell content spans the full column width; USDC sits
// at a fixed 82px right of the slash; icons sit immediately to the right
// of the USDC pill. Icons use text.secondary by default; trash turns
// error.main only on hover so the column doesn't read as "red" at rest.
export const InstrumentCell: FC<InstrumentCellProps> = ({
  instrumentId,
  columnIndex,
  atCapacity,
  onDuplicate,
  onRemove,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  const { lockedToken } = useContext(PricingContext);
  const { tokens: availableTokens } = useAvailableTokens();
  const theme = useTheme();
  // Guard before any field access — useInstrument can return undefined for
  // a stale id mid-removal. Placed after all hooks so hook order is stable.
  if (!inst) return null;
  // Live registry → list of base_quote strings. Fall back to BTC/ETH
  // while the first request is in flight so the dropdown isn't empty.
  const tokenOptions: Token[] =
    availableTokens.length > 0 ? availableTokens.map((t) => t.baseQuote) : TOKEN_OPTIONS_FALLBACK;
  // Map base_quote → display label (the base ccy only — "TRUMP_USDT" is
  // shown as "TRUMP" since the USDC/USDT slot to the right says the rest).
  const labelFor = (baseQuote: string): string => {
    const found = availableTokens.find((t) => t.baseQuote === baseQuote);
    return found?.baseCcy ?? baseQuote;
  };
  // Quote ccy shown in the right-hand pill — derive from the selected
  // token's registry entry, fall back to USDC.
  const selectedToken = availableTokens.find((t) => t.baseQuote === (lockedToken ?? inst.token));
  const quoteCcy = selectedToken?.quoteCcy ?? 'USDC';
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
      {/* Token and quote share equal flex so they sit at the same width
          regardless of column size; icons sit immediately to the right.
          When the surrounding tab locks the token, render a static pill
          (matches the disabled quote pill below) instead of the dropdown
          — the token is determined at the tab level and cannot vary
          per column inside a single-token tab. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {lockedToken ? (
          // Locked token is display-only, but must read CLEARLY (IM-1955 #4):
          // no faded/disabled greying. Full-contrast text at the STANDARD weight
          // — i.e. identical to the normal (enabled) token dropdown, just locked.
          <Select
            size="small"
            fullWidth
            variant="outlined"
            value={lockedToken}
            disabled
            sx={{
              ...tightSelectSx,
              '& .MuiSelect-select.Mui-disabled': {
                WebkitTextFillColor: theme.palette.text.primary,
              },
            }}
            inputProps={{ 'aria-label': `token-${instrumentId}` }}
          >
            <MenuItem value={lockedToken} sx={{ fontSize: 12 }}>
              {labelFor(lockedToken)}
            </MenuItem>
          </Select>
        ) : (
          <Select
            size="small"
            fullWidth
            variant="outlined"
            value={inst.token}
            onChange={(e) => patch({ token: e.target.value as Token })}
            sx={tightSelectSx}
            inputProps={{ 'aria-label': `token-${instrumentId}` }}
            SelectDisplayProps={{ tabIndex: tabIndexFor(columnIndex, ROW.instrument) }}
          >
            {tokenOptions.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>
                {labelFor(t)}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>
      <Typography sx={{ color: 'text.disabled', fontSize: 11 }}>/</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Select
          size="small"
          fullWidth
          variant="outlined"
          value={quoteCcy}
          disabled
          sx={{ ...tightSelectSx, opacity: 0.7 }}
          inputProps={{ 'aria-label': `acc-${instrumentId}` }}
        >
          <MenuItem value={quoteCcy} sx={{ fontSize: 12 }}>
            {quoteCcy}
          </MenuItem>
        </Select>
      </Box>
      <Tooltip title="Duplicate">
        <span>
          <IconButton
            size="small"
            disabled={atCapacity}
            onClick={() => onDuplicate(instrumentId)}
            aria-label={`duplicate-${instrumentId}`}
            tabIndex={-1}
            sx={{
              p: 0.5,
              color: 'text.secondary',
              '&:hover': { color: 'primary.light' },
            }}
          >
            <FontAwesomeIcon icon={faClone} style={{ fontSize: 10 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={() => onRemove(instrumentId)}
          aria-label={`remove-${instrumentId}`}
          tabIndex={-1}
          sx={{
            p: 0.5,
            color: 'text.secondary',
            '&:hover': { color: 'error.main' },
          }}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Contract: full-width Select with 7 options
// ────────────────────────────────────────────────────────────────────────

export const ContractCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  return (
    <Select
      size="small"
      fullWidth
      variant="outlined"
      value={inst.contract}
      onChange={(e) => patch({ contract: e.target.value as ContractKind } as Partial<Instrument>)}
      sx={tightSelectSx}
      inputProps={{ 'aria-label': `contract-${instrumentId}` }}
      SelectDisplayProps={{ tabIndex: tabIndexFor(columnIndex, ROW.contract) }}
    >
      {CONTRACT_OPTIONS.map(([kind, label]) => (
        <MenuItem key={kind} value={kind} sx={{ fontSize: 12 }}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Expiry: bespoke DatePicker (popover calendar)
// ────────────────────────────────────────────────────────────────────────

export const ExpiryCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  // Hooks must run unconditionally — fetch against '' (no-op) when there's
  // no instrument yet, before the early return below.
  const { expiries: listedExpiries } = useListedExpiries(inst?.token ?? '');
  if (!inst) return null;
  return (
    <DatePicker
      value={inst.expiry.slice(0, 10)}
      onChange={(next) => patch({ expiry: next })}
      ariaLabel={`expiry-${instrumentId}`}
      listedExpiries={listedExpiries}
      tabIndex={tabIndexFor(columnIndex, ROW.expiry)}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────
// Notional: [notionalToken Select | EditableNumber]
// For barrier contracts (notionalChoosable=false), the token chip is a
// disabled display of the underlying.
// ────────────────────────────────────────────────────────────────────────

export const NotionalCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
      {/* Notional token dropdown takes flex:1 so it ends up the same
          visible width as the token Select in the Instrument row. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {schema.notionalChoosable && 'notionalToken' in inst ? (
          <Select
            size="small"
            fullWidth
            variant="outlined"
            value={inst.notionalToken}
            onChange={(e) =>
              patch({ notionalToken: e.target.value as 'base' | 'quote' } as Partial<Instrument>)
            }
            sx={tightSelectSx}
            inputProps={{ 'aria-label': `notional-token-${instrumentId}` }}
            SelectDisplayProps={{ tabIndex: tabIndexFor(columnIndex, ROW.notional, 0) }}
          >
            <MenuItem value="quote" sx={{ fontSize: 12 }}>
              USDC
            </MenuItem>
            <MenuItem value="base" sx={{ fontSize: 12 }}>
              {inst.token}
            </MenuItem>
          </Select>
        ) : (
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 0.5,
              fontSize: 12,
              color: 'text.disabled',
              py: 0.5,
              px: 1,
            }}
          >
            {inst.token}
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <EditableNumber
          value={inst.notional}
          onCommit={(next) => patch({ notional: next })}
          // USDC notional shows 2dp (dollars/cents); base-token notional
          // shows 4dp because BTC/ETH amounts need the extra precision.
          format={
            'notionalToken' in inst && inst.notionalToken === 'quote' ? 'price-2dp' : 'price-4dp'
          }
          role="caption"
          ariaLabel={`notional-${instrumentId}`}
          allowUnitSuffix
          tabIndex={tabIndexFor(columnIndex, ROW.notional, 1)}
        />
      </Box>
    </Stack>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Strike — disabled when the contract doesn't declare a strike
// ────────────────────────────────────────────────────────────────────────

export const StrikeCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  if (!schema.strike || !('strike' in inst)) return <Disabled />;
  return (
    <EditableNumber
      value={inst.strike}
      onCommit={(next) => patch({ strike: next } as Partial<Instrument>)}
      format="price-2dp"
      role="caption"
      min={0}
      ariaLabel={`strike-${instrumentId}`}
      tabIndex={tabIndexFor(columnIndex, ROW.strike)}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────
// Upper Barrier
// ────────────────────────────────────────────────────────────────────────

export const UpperBarrierCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  if (!schema.upper || !('upper' in inst)) return <Disabled />;
  return (
    <EditableNumber
      value={inst.upper}
      onCommit={(next) => patch({ upper: next } as Partial<Instrument>)}
      format="price-2dp"
      role="caption"
      min={0}
      ariaLabel={`upper-${instrumentId}`}
      tabIndex={tabIndexFor(columnIndex, ROW.upper)}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────
// Lower Barrier
// ────────────────────────────────────────────────────────────────────────

export const LowerBarrierCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  if (!schema.lower || !('lower' in inst)) return <Disabled />;
  return (
    <EditableNumber
      value={inst.lower}
      onCommit={(next) => patch({ lower: next } as Partial<Instrument>)}
      format="price-2dp"
      role="caption"
      min={0}
      ariaLabel={`lower-${instrumentId}`}
      tabIndex={tabIndexFor(columnIndex, ROW.lower)}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────
// Pay Type — only for OneTouch (Upper/Lower)
// ────────────────────────────────────────────────────────────────────────

export const PayTypeCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  if (!schema.payType || !('payType' in inst)) return <Disabled />;
  return (
    <Select
      size="small"
      fullWidth
      variant="outlined"
      value={inst.payType}
      onChange={(e) => patch({ payType: e.target.value as PayType } as Partial<Instrument>)}
      sx={tightSelectSx}
      inputProps={{ 'aria-label': `payType-${instrumentId}` }}
      SelectDisplayProps={{ tabIndex: tabIndexFor(columnIndex, ROW.payType) }}
    >
      {PAY_TYPES.map((p) => (
        <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>
          {p}
        </MenuItem>
      ))}
    </Select>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Call / Put — for European, American, and SingleBarrier (Upper/Lower)
// ────────────────────────────────────────────────────────────────────────

export const CallPutCell: FC<{ instrumentId: string; columnIndex: number }> = ({
  instrumentId,
  columnIndex,
}) => {
  const { inst, patch } = useInstrument(instrumentId);
  if (!inst) return null;
  const schema = CONTRACT_SCHEMA[inst.contract];
  if (!schema.callPut || !('cp' in inst)) return <Disabled />;
  return (
    <Select
      size="small"
      fullWidth
      variant="outlined"
      value={inst.cp}
      onChange={(e) => patch({ cp: e.target.value as CallPut } as Partial<Instrument>)}
      sx={tightSelectSx}
      inputProps={{ 'aria-label': `cp-${instrumentId}` }}
      SelectDisplayProps={{ tabIndex: tabIndexFor(columnIndex, ROW.callPut) }}
    >
      {CALL_PUTS.map((cp) => (
        <MenuItem key={cp} value={cp} sx={{ fontSize: 12 }}>
          {cp}
        </MenuItem>
      ))}
    </Select>
  );
};
